<template>
  <v-layout
    :column="breakpoint.xs"
    class="mt-4 mb-2"
  >
    <v-flex
      class="pr-3 xs12 sm12"
    >
      <v-text-field
        ref="field"
        v-model="qrCode"
        label="QR"
        @change="getGradesOptions"
      />
      <v-btn
        v-if="!isMobile"
        :disabled="!qrCode"
        color="primary"
        outline
        @click="() => getGradesOptions(qrCode)"
      >
        グレードリストをもらう
      </v-btn>
    </v-flex>
    <v-flex>
      <v-btn
        v-if="isMobile && isApp"
        id="qr"
        :loading="loading"
        color="amber lighten-2"
        large
        class="ma-0 mb-5"
      >車検証QRコード読取!</v-btn>
      <cp-code-scanner
        v-if="!isApp"
        btn-color="primary"
        @scan-complete="scanComplete" />
      <v-dialog
        v-model="dialog"
        fullscreen
        persistent
        max-width="500"
      >
        <v-card>
          <v-card-title class="py-2 pr-0">
            <v-flex class="subheading">適当なパッケージを選択してください</v-flex>
            <v-spacer />
            <v-btn
              icon
              @click="close"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text v-if="qrParams.Year">
            <v-flex class="title pb-3">車検証QRコードの内容</v-flex>
            <v-layout
              wrap
              class="pb-3">
              <v-flex class="xs6 caption">初年度登録</v-flex>
              <v-flex class="xs6">{{ date(qrParams.Year) }}</v-flex>
              <v-flex class="xs6 caption">型式</v-flex>
              <v-flex class="xs6">{{ qrParams.Katashiki }}</v-flex>
              <v-flex class="xs6 caption">車体番号</v-flex>
              <v-flex class="xs6">{{ qrParams.VIN }}</v-flex>
            </v-layout>
            <v-divider />
          </v-card-text>
          <v-card-text v-if="uniques.length !== 1">
            <div
              v-if="grades[0] && grades[0].ModelNameJp && makersPages[grades[0].ModelNameJp]"
              class="py-3">
              <v-btn
                :href="makersPages[grades[0].ModelNameJp]"
                color="error"
                target="_blank">
                {{ grades[0].ModelNameJp }}グレード確認
              </v-btn>
            </div>
            <v-flex
              v-for="unique in uniques"
              :key="unique.id"
              class="bold"
            >
              <div class="subheading pb-3">{{ unique.Grade }}
                <span class="caption">
                  ({{ date(unique.StartSale) }}~{{ date(unique.EndSale) }})
                </span>
              </div>
              <v-layout
                v-for="field in fields(unique)"
                v-if="canShow(field, unique)"
                :key="field"
                align-center
                class="pb-1">
                <v-flex class="xs4 caption">{{ title(field) }}</v-flex>
                <v-flex class="xs6">{{ value(field, unique) }}</v-flex>
                <v-flex class="xs2 align-xs-right">
                  <v-btn
                    :disabled="confirmedFields.includes(field)"
                    :outline="!confirmedFields.includes(field)"
                    :dark="confirmedFields.includes(field)"
                    small
                    icon
                    color="amber darken-2"
                    fab
                    @click="filterByValue(field, unique)"
                  >
                    <v-icon>
                      check
                    </v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
              <v-btn
                outline
                color="primary"
                @click="uniques = uniques.filter(u => u === unique)"
              >選択</v-btn>
              <v-divider class="mt-2 mb-4" />
            </v-flex>
          </v-card-text>
          <v-card-text
            v-else>
            <div class="subheading pb-3">{{ modelName(bestMatch) }}</div>
            <v-layout
              v-for="field in fields(bestMatch)"
              v-if="canShow(field, bestMatch)"
              :key="field"
              class="pb-1">
              <v-flex class="xs6 caption">{{ title(field) }}</v-flex>
              <v-flex class="xs6">{{ value(field, bestMatch) }}</v-flex>
            </v-layout>
          </v-card-text>
          <v-layout
            column
            class="pa-3">
            <v-btn
              :disabled="uniques.length > 1"
              color="primary"
              block
              depressed
              class="px-2"
              @click="confirm"
            >グレード情報確認済み</v-btn>
            <v-btn
              color="default"
              outline
              block
              class="px-2"
              @click.native="close"
            >キャンセル</v-btn>
          </v-layout>
        </v-card>
      </v-dialog>
    </v-flex>
  </v-layout>
</template>

<script>
// Example:
// eslint-disable-next-line
// 2/-  /125130033/180731/0509/CBA-MB5244/0088/-   /-   /0060/10/096/2/0/-/-   /-    /999999/01@x@x@2/足立　　３５７て１７１７/A/YV1MW384962159573/B5244/1

import isWebview from 'is-ua-webview';
import { BASIC_TAB, getCarWith } from '../queries';
import GET_PACKAGES_BY_QR, { UPSERT_CAR, makersPages, fieldsLabels } from './catalog-consts';
import {
  transmissions,
  catalogEquipment,
  fieldsForInspectors,
  equipment,
  salesPoints,
} from './picker-options';

const invalidSts = '199999';
const invalidInspectionDate = '999999';
const invalidStsMonths = ['99', '00'];
const defaultStsDay = '01';
const defaultStsMonth = '03';

const CpCodeScanner = () => import('camera-ui/src/components/CpCodeScanner');

const getUniqueFields = (grade, grades) => Object
  .keys(grade)
  .filter(key => !key.match(/(Third|Entrapment|PowerSupply)/i)
    && key.match(fieldsForInspectors))
  .filter(key => grades.find(p => p[key] !== grade[key]));

const uniques = packages => packages.reduce((diff, grade) => {
  const rest = packages.filter(p => p.HoyuID !== grade.HoyuID);

  const fields = getUniqueFields(grade, rest);

  return [
    ...diff,
    fields.reduce((acc, cur) => ({
      ...acc,
      [cur]: grade[cur],
    }), { id: grade.HoyuID }),
  ];
}, []);

export default {
  components: { CpCodeScanner },
  apollo: {
    appointment: {
      query: getCarWith(),
      variables() {
        return { id: parseInt(this.$route.params.id, 10) };
      },
      result({ data }) {
        this.carId = data.appointment.car.id;
      },
    },
  },
  data() {
    const defaults = {
      qrParams: {},
      grades: [],
      uniques: [],
      values: {},
      confirmedFields: [],
    };

    return {
      showInput: true,
      qrCode: null,
      loading: false,
      dialog: false,
      carId: null,
      ...defaults,
      makersPages,
    };
  },
  computed: {
    bestMatch() {
      if (!this.grades || !this.uniques) return null;

      return this.grades.find(g => g.HoyuID === this.uniques[0].id);
    },
    isApp() {
      return isWebview(navigator.userAgent);
    },
  },
  watch: {
    qrCode(newVal) {
      if (newVal && newVal.length > 10) {
        this.getGradesOptions(newVal);
      }
    },
  },
  methods: {
    canShow(field, unique) {
      const hasField = field !== 'id' && this.title(field) && field !== 'Grade' && !field.includes('Sale');

      if (!hasField) {
        return false;
      }

      return !!this.value(field, unique);
    },
    modelName(grade) {
      return `${grade.ModelNameJp}${grade.BrandNameJp}${grade.Grade}`;
    },
    value(field, unique) {
      const value = unique[field];
      if (field === 'TransmissionId') {
        const transmission = transmissions.find(t => t.value === value);
        return transmission ? transmission.text : value;
      }

      const tableValue = catalogEquipment.find(e => e.value === value && e.field === field);

      switch (value) {
        case '●':
        case '○':
          return 'あり';
        default:
          return tableValue ? tableValue.text : value;
      }
    },
    title(field) {
      return fieldsLabels[field] || fieldsLabels[`Eq${field}`] || fieldsLabels[`${field}Existing`] || null;
    },
    fields(grade) {
      return Object.keys(grade);
    },
    getGradesOptions(qr) {
      if (!qr) return;

      this.loading = true;
      this.dialog = false;

      this.$apollo.query({
        query: GET_PACKAGES_BY_QR,
        variables: { qr },
      }).then(this.success).catch(e => this.error(e));
    },
    // Fields that are easy to check: seat, powerseat, handle leather, bumper spoiler
    success({ data }) {
      this.qrParams = data.gradesByQR.params;
      this.grades = data.gradesByQR.data;

      this.uniques = uniques(this.grades);

      this.loading = false;

      if (data.gradesByQR.data.length === 0) {
        this.$store.commit('setError', {
          message: 'このQRコードを読取できませんでした',
        });
        return;
      }
      this.dialog = true;
    },
    error() {
      this.loading = false;
      this.$store.commit('setError', {
        message: 'このQRコードを読取できませんでした',
      });
    },
    date(s) {
      if (!s || s.toString() === invalidSts || this.isInvalidStsMonth(s)) return '';

      const a = `${s}`;

      return `${a.slice(0, 4)}年${a.slice(4, 6)}月`;
    },
    filterByValue(field, grade) {
      this.confirmedFields = [...this.confirmedFields, field];
      this.uniques = this.uniques.filter(u => u[field] === grade[field]);
    },
    close() {
      this.dialog = false;
    },
    confirm() {
      this.savePackage({ ...this.bestMatch, ...this.qrParams });
      this.close();
    },
    yesOrNo(field) {
      return (field && field !== '×') ? 'YES' : 'NO';
    },
    getHeater(s = '') {
      if (!s) return 'NONE';

      if (s.includes('前' || 'D')) {
        return 'DRIVER';
      }

      if (s.includes('全')) {
        return 'ALL_SEATS';
      }

      return null;
    },
    getEngineType(fuelId) {
      switch (fuelId) {
        case 'G':
        case 'GH':
          return 'GASOLINE';
        case 'D':
        case 'DH':
          return 'DIESEL';
        case 'E':
          return 'ELECTRIC';
        case 'L':
          return 'LPG';
        default:
          return null;
      }
    },
    getNumberPlate() {
      if (!this.qrCode) return null;
      const qrCodes = this.qrCode.split('@x@x@');
      const parts = qrCodes[1].split('/');
      switch (parts[0]) {
        case '2':
          return parts[1];
        case 'K':
          return parts[2];
        default:
          return null;
      }
    },
    async savePackage(data) {
      const { Year: sts, ShakenFinishDate: i } = data;

      const d = (s, from, to) => s.toString().substring(from, to);

      const carData = {
        id: this.carId,
        vin: data.VIN,
        engineModel: data.EngineModel,
        hoyuId: data.HoyuID,
        engine: `${data.engine * 0.001}`,
        engineType: this.getEngineType(data.FuelId),
        plate: data.CertifiedModel,
        transmission: (data.TransmissionId || '').includes('M') ? 'MT' : 'AT',
        transmissionDrive: data.Drive,
        modificationExt: data.Grade,
        seats: `${data.CountHumans}`,
        numberPlate: this.getNumberPlate(),
      };

      if (sts.toString() !== invalidSts) {
        const inValidStsMonth = this.isInvalidStsMonth(sts);
        carData.sts = `${d(sts, 0, 4)}-${inValidStsMonth ? defaultStsMonth : d(sts, 4, 6)}-${defaultStsDay}`;
        carData.isDefaultStsMonth = inValidStsMonth ? 1 : 0;
      }

      if (i.toString() !== invalidInspectionDate) {
        carData.inspectionDate = `20${d(i, 0, 2)}-${d(i, 2, 4)}-${d(i, 4, 6)}`;
      }

      await this.$apollo.mutate({
        mutation: UPSERT_CAR,
        refetchQueries: [{
          query: BASIC_TAB,
          variables: { id: parseInt(this.$route.params.id, 10) },
        }],
        variables: {
          data: carData,
        },
      });

      Object.keys(equipment).forEach(
        async code => this.updateField({
          code,
          value: this.yesOrNo(data[equipment[code]]),
          carId: this.carId,
          typeId: 79,
        }));

      Object.keys(salesPoints).forEach(
        async code => this.updateField({
          code,
          value: code === 'SEAT_HEATER'
          ? this.getHeater(data.HeaterSheet)
          : this.yesOrNo(data[salesPoints[code]]),
          carId: this.carId,
          typeId: 80,
        }));

      this.$emit(
        'gradeSelected', {
          // Car Master brand and model name is inconsistent with the Catalog DB
          brandText: data.ModelNameJp,
          modelText: data.BrandNameJp,
          gradeText: data.Grade,
          typeText: data.CertifiedModelWithoutEmissionLevel,
        },
      );
    },
    scanComplete(result) {
      const decodedString = result.string;
      this.qrCode = decodedString;
    },
    isInvalidStsMonth(sts) {
      return invalidStsMonths.includes(sts.toString().slice(-2));
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/PackagePicker.vue